import { PointerEvent as ReactPointerEvent } from 'react';

import { Camera, CanvasElement } from '@/features/canvas/types';
import { worldToScreen } from './geometry';

const ACCENT = '#2ec5e8';
const OUT = 14; // screen px each nub sits OUTSIDE the edge (clear of the resize handles on the border)

// N / E / S / W edge midpoints, with an outward unit offset.
const NUBS = [
  { dx: 0.5, dy: 0, ox: 0, oy: -1 },
  { dx: 1, dy: 0.5, ox: 1, oy: 0 },
  { dx: 0.5, dy: 1, ox: 0, oy: 1 },
  { dx: 0, dy: 0.5, ox: -1, oy: 0 },
];

/**
 * Miro-style connection nubs: four dots just outside a hovered element's edges. Pressing a nub and
 * dragging to another element draws a connector — no connector tool needed. Rendered in screen space
 * so the nubs stay a constant size and clear of the on-border resize handles.
 */
export function ConnectionNubs({
  cam,
  el,
  onStart,
}: {
  cam: Camera;
  el: CanvasElement | undefined;
  onStart: (e: ReactPointerEvent, id: string, fromEdge?: boolean) => void;
}) {
  if (!el) return null;
  const tl = worldToScreen(cam, el.x, el.y);
  // A single ROTATED frame carries the outline and the nubs, so both track a rotated element's edges
  // (the nubs sit OUT px outside each edge in the element's own axes).
  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      <div
        style={{
          position: 'absolute',
          left: tl.x,
          top: tl.y,
          width: el.w * cam.zoom,
          height: el.h * cam.zoom,
          transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
          transformOrigin: 'center center',
        }}
      >
        <div className="absolute inset-0 rounded-[3px] border" style={{ borderColor: `${ACCENT}66` }} />
        {NUBS.map((n, i) => (
          <div
            key={i}
            onPointerDown={(e) => onStart(e, el.id, true)}
            title="Drag to connect"
            className="pointer-events-auto absolute rounded-full border-2 bg-white shadow-sm transition-transform hover:scale-125"
            style={{
              left: `calc(${n.dx * 100}% + ${n.ox * OUT}px - 6px)`,
              top: `calc(${n.dy * 100}% + ${n.oy * OUT}px - 6px)`,
              width: 12,
              height: 12,
              borderColor: ACCENT,
              cursor: 'crosshair',
            }}
          />
        ))}
      </div>
    </div>
  );
}

/** A highlight ring around the element a connector-in-progress would attach to (drop target feedback). */
export function TargetHighlight({ cam, el }: { cam: Camera; el: CanvasElement | undefined }) {
  if (!el) return null;
  const s = worldToScreen(cam, el.x, el.y);
  return (
    <div
      className="pointer-events-none absolute z-20 rounded-[6px] border-2"
      style={{
        left: s.x - 2,
        top: s.y - 2,
        width: el.w * cam.zoom + 4,
        height: el.h * cam.zoom + 4,
        borderColor: ACCENT,
        transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
        transformOrigin: 'center center',
      }}
    />
  );
}
